import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Guest,
  GuestStats,
  CreateGuestInput,
  UpdateGuestInput,
  UpdateRsvpInput,
  BulkCreateGuestsInput,
  BulkUpdateGuestsInput,
  BulkDeleteGuestsInput,
  BulkMutationResult,
} from '@peles/shared';
import * as guestsApi from '../api/guests';
import type { GuestFilters } from '../api/guests';

export function useGuests(filters?: GuestFilters) {
  return useQuery<Guest[]>({
    queryKey: ['guests', filters ?? 'all'],
    queryFn: () => guestsApi.getGuests(filters),
  });
}

export function useGuestStats(eventId?: string) {
  return useQuery<GuestStats>({
    queryKey: ['guests', 'stats', eventId],
    queryFn: () => guestsApi.getGuestStats(eventId!),
    enabled: !!eventId,
  });
}

export function useGuestFamilyGroups(eventId?: string) {
  return useQuery<string[]>({
    queryKey: ['guests', 'family-groups', eventId ?? 'all'],
    queryFn: () => guestsApi.getGuestFamilyGroups(eventId),
  });
}

export function useCreateGuest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateGuestInput) => guestsApi.createGuest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useUpdateGuest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateGuestInput }) =>
      guestsApi.updateGuest(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useDeleteGuest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => guestsApi.deleteGuest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useUpdateRsvp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRsvpInput }) =>
      guestsApi.updateRsvp(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useBulkCreateGuests() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkCreateGuestsInput) => guestsApi.bulkCreateGuests(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useBulkUpdateGuests() {
  const queryClient = useQueryClient();
  return useMutation<BulkMutationResult, Error, BulkUpdateGuestsInput>({
    mutationFn: (data) => guestsApi.bulkUpdateGuests(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useBulkDeleteGuests() {
  const queryClient = useQueryClient();
  return useMutation<BulkMutationResult, Error, BulkDeleteGuestsInput>({
    mutationFn: (data) => guestsApi.bulkDeleteGuests(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}
